import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ShoppingBag, UtensilsCrossed, Music, Gift, X, ChevronRight } from "lucide-react";
import { trackActivitySpotlightImpression, trackActivitySpotlightClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const ACTIVITIES = [
  {
    id: "buy-sell-trade",
    icon: ShoppingBag,
    title: "Buy · Sell · Trade",
    description: "Bring your own sneakers, heat, and gear. Buy, sell, or trade on the spot.",
  },
  {
    id: "food-drinks",
    icon: UtensilsCrossed,
    title: "Food & Drinks",
    description: "Vendors and concessions on site so you can stay fueled all event.",
  },
  {
    id: "music-dj",
    icon: Music,
    title: "Music / DJ",
    description: "Live DJ sets keeping the energy up while you shop.",
  },
  {
    id: "giveaways",
    icon: Gift,
    title: "Giveaways",
    description: "Free raffles and giveaways at every Sole to Sole event.",
  },
];

const STORAGE_KEY = "activity_spotlight_dismissed";
const SCROLL_THRESHOLD = 0.3;
const ROTATION_INTERVAL_MS = 4000;

const SUPPRESSED_PATHS = ["/admin", "/apply"];

const ActivitySpotlight = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [index, setIndex] = useState(0);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isSuppressed = SUPPRESSED_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    setExpanded(false);
  }, []);

  useEffect(() => {
    if (isSuppressed) return;

    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = docHeight > 0 ? scrollTop / docHeight : 0;
      if (scrolled >= SCROLL_THRESHOLD) {
        setVisible(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isSuppressed]);

  useEffect(() => {
    if (!visible || hasTrackedImpression) return;
    trackActivitySpotlightImpression(ACTIVITIES[index].id);
    setHasTrackedImpression(true);
  }, [visible, index, hasTrackedImpression]);

  useEffect(() => {
    if (!visible) return;
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % ACTIVITIES.length);
    }, ROTATION_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [visible]);

  const handleExpand = () => {
    setExpanded(true);
    trackActivitySpotlightClick(ACTIVITIES[index].id, "expand");
  };

  const handleLearnMore = () => {
    trackActivitySpotlightClick(ACTIVITIES[index].id, "learn_more");
    window.location.href = "/about";
  };

  if (!visible || isSuppressed) return null;

  const activity = ACTIVITIES[index];
  const Icon = activity.icon;

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 transition-all duration-300 ease-out",
        expanded ? "w-72" : "w-auto"
      )}
      aria-live="polite"
    >
      {expanded ? (
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 relative animate-fade-in">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-muted transition-colors"
            aria-label="Close event highlights"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h4 className="font-heading text-sm font-bold text-card-foreground leading-tight">
                {activity.title}
              </h4>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {activity.description}
              </p>
            </div>
          </div>

          <button
            onClick={handleLearnMore}
            className="w-full mt-3 flex items-center justify-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            Learn more about events
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={handleExpand}
          className="group w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label="Show event highlights"
        >
          <Icon className="w-6 h-6 transition-transform duration-300 group-hover:rotate-12" />
        </button>
      )}
    </div>
  );
};

export default ActivitySpotlight;
